<script setup lang="ts">
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'

import CopyEntityLink from '@/components/createdUi/CopyEntityLink.vue'
import EntityPaneEar from '@/components/createdUi/EntityPaneEar.vue'
import DownloadEntityDocument from '@/components/createdUi/DownloadEntityDocument.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import EntityAccessDeniedOverlay from '@/components/createdUi/EntityAccessDeniedOverlay.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import ShortFio from '@/components/createdUi/ShortFio.vue'
import { type ISearchableSelectItem } from '@/components/createdUi/SearchableSelectList.vue'
import BridgedEmbeddedChat from '@/components/chat/BridgedEmbeddedChat.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { useEmbeddedChatSetup } from '@/composables/useChatModuleBridge'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import FileAttach from '@/modules/files/components/FileAttach.vue'
import FileList from '@/modules/files/components/FileList.vue'
import TaskCard from '@/modules/tasks/components/TaskCard.vue'
import { useImprovementsStore } from '@/modules/improvements/stores/improvementsStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { useUserStore } from '@/modules/user/stores/userStore'
import { ERouteNames } from '@/router/ERouteNames'
import type { IHistoryLog } from '@/types/IHistoryLog'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify/framework'

import type { IEmployeeTreeDepartment, IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import type { IPpuHistoryItem } from '@/modules/improvements/types/IImprovementSuggestion'
import type { IUpdateSuggestionPayload } from '@/modules/improvements/types/IPpuPayloads'
import type { IResponsibilityEntry } from '@/modules/tasks/types/IResponsibilityEntry'
import {
  getPpuAggregatedStatusColor,
  getPpuAggregatedStatusLabel,
  getPpuStatusColor,
  getPpuStatusLabel,
} from '@/modules/improvements/utils/ppuStatusHelper'
import { formatDateTime, getFullName } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'
import { isHttpNotFound } from '@/utils/httpErrors'
import OnboardingDemoChat from '@/modules/onboarding/components/OnboardingDemoChat.vue'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { IMPROVEMENTS_ANCHORS, IMPROVEMENTS_CONTEXTS } from '@/modules/onboarding/scenarios/improvements/anchors'
import { IMPROVEMENTS_FORM_DEMO_INDEX } from '@/modules/onboarding/scenarios/improvements/adapter'

const route = useRoute()
const router = useRouter()

const props = withDefaults(
  defineProps<{
    modelValue?: boolean
    suggestionId?: number | null
    isGlobal?: boolean
  }>(),
  {
    modelValue: false,
    suggestionId: null,
    isGlobal: false,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'updated'): void
}>()

const { xs, smAndDown } = useDisplay()
const improvementsStore = useImprovementsStore()
const userStore = useUserStore()
const apiStore = useApiStore()
const feedbackStore = useFeedbackStore()

const isOpen = computed({
  get: () => (props.isGlobal ? true : props.modelValue),
  set: (val: boolean) => {
    if (!props.isGlobal) emit('update:modelValue', val)
  },
})

const targetSuggestionId = computed<number | null>(() => {
  if (props.suggestionId) return props.suggestionId
  if (route.params.id) {
    const parsed = Number(route.params.id)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }
  return null
})

const isEditMode = computed(() => {
  const id = improvementsStore.currentSuggestion?.id ?? targetSuggestionId.value
  return isOpen.value && typeof id === 'number' && Number.isFinite(id)
})

/*
 * Демо-режим онбординга: карточка открыта на учебном ППУ (id из демо-данных онбординга)
 * или онбординг запущен из карточки реального ППУ (demoOverrideId).
 * Id учебного ППУ фиктивный — по нему ничего не запрашиваем и Matrix-чат не открываем.
 */
const onboardingStore = useOnboardingStore()
const demoOverrideId = ref<number | null>(null)
const demoItem = computed(() => onboardingStore.findDemoItem('improvement', demoOverrideId.value ?? targetSuggestionId.value))
const isDemo = computed(() => Boolean(demoItem.value))
const isChatEnabled = computed(() => isEditMode.value && !isDemo.value)

// Учебная переписка в правой панели вместо Matrix-чата
const isDemoChatVisible = ref(false)

function applyDemoSuggestion() {
  if (!demoItem.value)
    return

  improvementsStore.currentSuggestion = JSON.parse(JSON.stringify(demoItem.value.detail))
  improvementsStore.editableSuggestion = JSON.parse(JSON.stringify(demoItem.value.detail))
  isAccessDenied.value = false
}

const { formEmbeddedLayout, entityChat, chatPaneResizer } = useEmbeddedChatSetup({
  isEditMode,
  chatEnabled: isChatEnabled,
  chat: {
    appLabel: 'improvements',
    modelName: 'ImprovementSuggestion',
    objectId: computed(() => {
      const id = targetSuggestionId.value
      return !isDemo.value && typeof id === 'number' && Number.isFinite(id) ? id : null
    }),
  },
  storageKey: 'ppu-chat-pane',
})

const isTasksPaneVisible = ref(false)

/*
 * Панель задач — только когда задача координатора уже есть. В отличие от НС и заявок, её не назначают вручную:
 * бэк создаёт её на председателя комиссии при попадании ППУ в комиссию (до этого, на согласовании у руководителя, задачи нет).
 */
const hasTasksPane = computed(() => Boolean(suggestion.value?.coordinator_entry))

const isRightPaneVisible = computed(() =>
  (entityChat.isChatVisible.value && entityChat.isAvailable.value)
  || (isTasksPaneVisible.value && hasTasksPane.value)
  || isDemoChatVisible.value,
)

const overlayContentClass = computed(() => {
  if (formEmbeddedLayout.contentClass.value) {
    return [
      formEmbeddedLayout.contentClass.value,
      { 'entity-form-embedded--no-chat': !isRightPaneVisible.value },
    ]
  }
  return undefined
})

const coordinatorTask = computed(() => {
  if (!suggestion.value?.coordinator_entry) return null
  return {
    ...suggestion.value.coordinator_entry,
    is_coordinator_entry: true,
    source_type: 'improvementsuggestion' as const,
    url_props: {
      suggestionId: targetSuggestionId.value ? Number(targetSuggestionId.value) : undefined,
    },
  } as any
})

const handleCoordinatorTaskUpdated = (updatedTask: any) => {
  if (suggestion.value?.coordinator_entry) {
    suggestion.value.coordinator_entry = {
      ...suggestion.value.coordinator_entry,
      ...updatedTask,
    }
    if (improvementsStore.editableSuggestion?.coordinator_entry) {
      improvementsStore.editableSuggestion.coordinator_entry = {
        ...improvementsStore.editableSuggestion.coordinator_entry,
        ...updatedTask,
      }
    }
  }
}

/*
 * «Ушки» задач и чата работают как вкладки правой панели (как в форме аудита):
 * клик по неактивному — переключает панель на него, по активному — скрывает панель.
 */
const isTasksPaneActive = computed(() => isTasksPaneVisible.value && hasTasksPane.value)
const isChatPaneActive = computed(() => !isTasksPaneActive.value && entityChat.isChatVisible.value && entityChat.isAvailable.value)

// Без ушек (мобилка) правой панели нет — задачи открываются под шапкой формы вместо её тела
const isMobileTasksView = computed(() => !formEmbeddedLayout.isEmbedded.value && isTasksPaneActive.value)

function toggleDemoChat() {
  isDemoChatVisible.value = !isDemoChatVisible.value
  if (isDemoChatVisible.value)
    isTasksPaneVisible.value = false
}

function toggleTasksPane() {
  if (isTasksPaneActive.value) {
    isTasksPaneVisible.value = false
    return
  }
  isTasksPaneVisible.value = true
  isDemoChatVisible.value = false
  if (entityChat.isChatVisible.value)
    entityChat.hideChat()
}

function toggleChatPane() {
  if (isChatPaneActive.value) {
    entityChat.hideChat()
    return
  }
  isTasksPaneVisible.value = false
  entityChat.showChat()
}

watch(entityChat.isChatVisible, val => {
  if (val && isTasksPaneVisible.value) {
    isTasksPaneVisible.value = false
  }
})

// globalFormTab = 2 — сигнал «открыть панель задач»; immediate, т.к. выставляется до монтирования формы
watch(() => feedbackStore.globalFormTab, tab => {
  if (tab === 2) {
    isTasksPaneVisible.value = true
    if (entityChat.isChatVisible.value) {
      entityChat.hideChat()
    }
    feedbackStore.globalFormTab = null
  }
}, { immediate: true })

const permissionsStore = usePermissionsStore()
const fileAttachRef = ref<InstanceType<typeof FileAttach> | null>(null)
const currentTab = ref(0)
const isAccessDenied = ref(false)


// Form fields (прямо привязаны к editableSuggestion в сторе по стандарту НС и Заявок)
const editableSuggestion = computed(() => improvementsStore.editableSuggestion)

const editableTitle = computed({
  get: () => editableSuggestion.value?.title || '',
  set: (val: string) => {
    if (editableSuggestion.value) editableSuggestion.value.title = val
  },
})

const editableTypeId = computed({
  get: () => editableSuggestion.value?.suggestion_type?.id || null,
  set: (val: string | null) => {
    if (editableSuggestion.value) {
      if (val === null) {
        editableSuggestion.value.suggestion_type = null
      } else {
        const found = improvementsStore.availableSuggestionTypes.find(t => t.id === val)
          || improvementsStore.suggestionTypes.find(t => t.id === val)
        if (found) editableSuggestion.value.suggestion_type = found
      }
    }
  },
})

const editableText = computed({
  get: () => editableSuggestion.value?.text || '',
  set: (val: string) => {
    if (editableSuggestion.value) editableSuggestion.value.text = val
  },
})

const editableExpectedResult = computed({
  get: () => editableSuggestion.value?.expected_result || '',
  set: (val: string) => {
    if (editableSuggestion.value) editableSuggestion.value.expected_result = val
  },
})

// Dialogs & sub-modals
const showEmployeeSelectionModal = ref(false)
const showReturnModal = ref(false)
const returnComment = ref('')
const returnTarget = ref<'author' | 'department-head'>('author')

const showRejectModal = ref(false)
const rejectComment = ref('')

const showAcceptModal = ref(false)
const acceptComment = ref('')

const showVoteModal = ref(false)
const voteComment = ref('')

const showDeleteExpertConfirm = ref(false)
const expertIdToDelete = ref<number | null>(null)
const showUnsavedChangesDialog = ref(false)

const isEmployeeInfoModalOpen = ref(false)
const selectedEmployeeId = ref<string | null>(null)
const selectedExpertId = ref<string | null>(null)



const suggestion = computed(() => improvementsStore.currentSuggestion)

const shareSuggestionId = computed(() =>
  suggestion.value?.id ?? targetSuggestionId.value,
)

const shareSuggestionTo = computed(() => {
  if (!shareSuggestionId.value)
    return undefined

  return {
    name: ERouteNames.IMPROVEMENT_DETAIL,
    params: { id: shareSuggestionId.value },
  }
})

const aggregatedStatusLabel = computed(() =>
  getPpuAggregatedStatusLabel(suggestion.value?.status, suggestion.value?.status_display),
)

const aggregatedStatusColor = computed(() => getPpuAggregatedStatusColor(suggestion.value?.status))

const stageStatusLabel = computed(() =>
  getPpuStatusLabel(
    suggestion.value?.stage || suggestion.value?.workflow_status,
    suggestion.value?.stage_display || suggestion.value?.workflow_status_display,
  ),
)

const stageStatusColor = computed(() =>
  getPpuStatusColor(suggestion.value?.stage || suggestion.value?.workflow_status),
)

const latestReviewComment = computed(() => suggestion.value?.latest_review_comment ?? null)

const reviewCommentLabel = computed(() => {
  const stage = suggestion.value?.stage || suggestion.value?.workflow_status
  if (
    stage === 'returned_to_author'
    || stage === 'on_revision'
    || stage === 'pending_department_head'
    || latestReviewComment.value?.comment_type === 'return'
  ) {
    return 'Комментарий к доработке'
  }
  return 'Решение'
})

const historyLogs = computed<IHistoryLog[]>(() => {
  if (!suggestion.value?.history?.length) return []
  return suggestion.value.history.map((h: IPpuHistoryItem) => ({
    id: h.id,
    cid: null,
    action_display: h.action_display || h.action || 'Изменение',
    object_repr: '',
    object_display: h.comment || '',
    changes_display: {},
    created_by: h.created_by
      ? {
          id: String(h.created_by.id),
          first_name: h.created_by.first_name || '',
          last_name: h.created_by.last_name || '',
          middle_name: h.created_by.middle_name || '',
          avatar: h.created_by.avatar || null,
          department_name: h.created_by.department_name || null,
          position_name: h.created_by.position_name || null,
        }
      : null,
    created_at: h.created_at,
  }))
})

const hasHistoryLogs = computed(() => historyLogs.value.length > 0)

function openEmployeeDetail(id: string) {
  selectedEmployeeId.value = id
  isEmployeeInfoModalOpen.value = true
}

const summaryItems = computed(() => [
  { label: 'Предложение', value: suggestion.value?.title },
  { label: 'Вид ППУ', value: suggestion.value?.suggestion_type?.name },
  { label: 'Описание', value: suggestion.value?.text },
  { label: 'Результат', value: suggestion.value?.expected_result },
])

const availableActions = computed(() => suggestion.value?.actions || [])
const editableFields = computed(() => suggestion.value?.editable_fields || [])

const isTitleEditable = computed(() => editableFields.value.includes('title'))
const isTypeEditable = computed(() => editableFields.value.includes('suggestion_type'))
const isTextEditable = computed(() => editableFields.value.includes('text'))
const isExpectedResultEditable = computed(() => editableFields.value.includes('expected_result'))
const isFilesEditable = computed(() => editableFields.value.includes('files'))

const isFormDirty = computed(() => {
  if (!suggestion.value) return false
  return (
    editableTitle.value !== (suggestion.value.title || '') ||
    editableTypeId.value !== (suggestion.value.suggestion_type?.id || null) ||
    editableText.value !== (suggestion.value.text || '') ||
    editableExpectedResult.value !== (suggestion.value.expected_result || '') ||
    selectedFilesToUpload.value.length > 0
  )
})

const showTypeMenu = ref(false)

const typeOptions = computed(() => {
  return improvementsStore.availableSuggestionTypes.map(t => ({
    title: t.name,
    value: t.id,
  }))
})

const typeSelectItems = computed<ISearchableSelectItem[]>(() => {
  const items = improvementsStore.availableSuggestionTypes.map(t => ({
    id: t.id,
    label: t.name,
  }))
  const current = suggestion.value?.suggestion_type
  if (current && !items.some(item => String(item.id) === String(current.id))) {
    items.unshift({ id: current.id, label: current.name })
  }
  return items
})

const selectedTypeName = computed(() => {
  const found = improvementsStore.availableSuggestionTypes.find(t => t.id === editableTypeId.value)
    || improvementsStore.suggestionTypes.find(t => t.id === editableTypeId.value)

  // Вида нет в справочниках (например, учебное ППУ онбординга) — берём имя из самой карточки
  return found ? found.name : (suggestion.value?.suggestion_type?.name ?? '')
})

function onSelectType(item: ISearchableSelectItem) {
  editableTypeId.value = String(item.id)
  showTypeMenu.value = false
}

function onClearType() {
  editableTypeId.value = null
}

// Experts list (agreement responsibility entries / subtasks)
const expertEntries = computed<IResponsibilityEntry[]>(() => {
  if (!suggestion.value) return []
  const subtasks = (suggestion.value.coordinator_entry as any)?.subtasks
  if (Array.isArray(subtasks) && subtasks.length > 0) {
    return subtasks
  }
  return (suggestion.value as any).performers_entries || []
})

const expertTableHeaders = computed(() => {
  const base: Array<{ title: string; key: string; sortable?: boolean; width?: string; align?: 'start' | 'center' | 'end' }> = [
    { title: '№', key: 'number', sortable: false, width: '50px', align: 'start' },
    { title: 'Эксперт', key: 'responsible_employee', sortable: false, align: 'start' },
    { title: 'Дата назначения', key: 'created_at', sortable: false, align: 'start' },
    { title: 'Статус голосования', key: 'status', sortable: false, align: 'start' },
  ]
  if (isChair.value) {
    base.push({ title: 'Комментарий', key: 'comment', sortable: false, align: 'start' })
  }
  if (expertEntries.value.some(entry => entry.actions?.includes('manage'))) {
    base.push({ title: 'Действия', key: 'delete_expert', sortable: false, align: 'center', width: '60px' })
  }
  return base
})

// Current user's expert entry for voting
const myExpertEntry = computed(() => {
  if (!suggestion.value || !userStore.user?.id) return null
  const currentEmpId = userStore.user.id
  return expertEntries.value.find(
    e => String(e.responsible_employee?.id) === String(currentEmpId) && e.type === 'agreement',
  )
})

const canVote = computed(() => {
  return Boolean(myExpertEntry.value?.actions?.includes('agree'))
})

// Voting stats
const totalVotedCount = computed(() => {
  return expertEntries.value.filter(e => e.status === 'completed' || e.agreement_result).length
})

const likesCount = computed(() => {
  return expertEntries.value.filter(e => e.agreement_result === 'confirmed').length
})

const dislikesCount = computed(() => {
  return expertEntries.value.filter(e => e.agreement_result === 'rejected').length
})

const likesPercent = computed(() => {
  if (!totalVotedCount.value) return 0
  return Math.round((likesCount.value / totalVotedCount.value) * 100)
})

const dislikesPercent = computed(() => {
  if (!totalVotedCount.value) return 0
  return Math.round((dislikesCount.value / totalVotedCount.value) * 100)
})

// Check if current user is Chair / Leader
const isChair = computed(() => {
  if (!suggestion.value || !userStore.user?.id) return false
  const chairId = suggestion.value.commission_chair?.id || suggestion.value.suggestion_type?.chair?.id
  const currentEmpId = userStore.user.id
  if (chairId && String(chairId) === String(currentEmpId)) return true
  // Check if user has commission action
  return availableActions.value.includes('commission')
})


const isReviewTabVisible = computed(() => {
  if (!suggestion.value) return false
  return (
    suggestion.value.reached_commission ||
    !!suggestion.value.coordinator_entry ||
    expertEntries.value.length > 0 ||
    isChair.value ||
    !!myExpertEntry.value
  )
})

const chairName = computed(() => {
  if (suggestion.value?.commission_chair) {
    return getFullName(suggestion.value.commission_chair)
  }
  if (suggestion.value?.suggestion_type?.chair) {
    return getFullName(suggestion.value.suggestion_type.chair)
  }
  return 'Не назначен'
})

watch(
  targetSuggestionId,
  async (newId) => {
    currentTab.value = 0
    if (newId) {
      isOpen.value = true
      await loadSuggestion(newId)
    } else {
      isAccessDenied.value = false
    }
  },
  { immediate: true },
)

async function loadSuggestion(id: number) {
  isAccessDenied.value = false
  if (isDemo.value) {
    applyDemoSuggestion()
    return
  }
  try {
    if (!improvementsStore.availableSuggestionTypes.length) {
      await improvementsStore.fetchAvailableSuggestionTypes()
    }
    await improvementsStore.fetchSuggestionById(id)
  } catch (e) {
    if (isHttpNotFound(e))
      isAccessDenied.value = true
  }
}

function handleClose() {
  if (isFormDirty.value) {
    showUnsavedChangesDialog.value = true
  } else {
    forceClose()
  }
}

const onDialogDismissAttempt = useDialogDismissGuard(handleClose)

/*
 * Онбординг: форма — контекст шагов по полям, вкладке «Рассмотрение» и «ушкам», панель задачи — вложенный контекст.
 * showDemo/showReal подменяют реальное ППУ учебным, когда обучение запущено из открытой карточки.
 */
useOnboardingContext({
  names: [IMPROVEMENTS_CONTEXTS.form, IMPROVEMENTS_CONTEXTS.formTasks],
  scenarioId: 'improvements.form',
  root: () => document.body,
  actions: {
    showDemo: () => {
      demoOverrideId.value = onboardingStore.demo.improvement?.[IMPROVEMENTS_FORM_DEMO_INDEX]?.detail.id ?? null
      currentTab.value = 0
      applyDemoSuggestion()
    },
    showReal: async () => {
      demoOverrideId.value = null
      isTasksPaneVisible.value = false
      isDemoChatVisible.value = false
      currentTab.value = 0
      if (targetSuggestionId.value && !isDemo.value)
        await loadSuggestion(targetSuggestionId.value)
    },
    openReviewTab: () => { currentTab.value = 1 },
    closeReviewTab: () => { currentTab.value = 0 },
    openTasksPane: () => {
      if (!isTasksPaneActive.value)
        toggleTasksPane()
    },
    closeTasksPane: () => { isTasksPaneVisible.value = false },
    openDemoChat: () => {
      if (!isDemoChatVisible.value)
        toggleDemoChat()
    },
    closeDemoChat: () => { isDemoChatVisible.value = false },
  },
})

function forceClose() {
  showUnsavedChangesDialog.value = false
  isTasksPaneVisible.value = false
  currentTab.value = 0
  if (props.isGlobal) {
    feedbackStore.globalImprovementId = null
  } else {
    isOpen.value = false
    if (route.name === ERouteNames.IMPROVEMENT_DETAIL) {
      router.replace({ name: ERouteNames.IMPROVEMENTS })
    }
  }
}

function openEmployeeProfile(empId?: string) {
  if (empId) {
    selectedEmployeeId.value = String(empId)
    isEmployeeInfoModalOpen.value = true
  }
}


const selectedFilesToUpload = ref<File[]>([])

// Upload/remove files

async function onFileRemoved(index: number) {
  const file = suggestion.value?.files?.[index]
  if (file && props.suggestionId) {
    const ok = await improvementsStore.deleteSuggestionFile(props.suggestionId, file.id)
    if (ok) {
      await loadSuggestion(props.suggestionId)
      emit('updated')
    }
  }
}

// Actions
async function onSaveForm() {
  if (!props.suggestionId) return

  if (selectedFilesToUpload.value.length) {
    await improvementsStore.uploadSuggestionFiles(props.suggestionId, selectedFilesToUpload.value)
    selectedFilesToUpload.value = []
  }

  const payload: IUpdateSuggestionPayload = {}
  if (isTitleEditable.value) payload.title = editableTitle.value
  if (isTypeEditable.value && editableTypeId.value) payload.suggestion_type = { id: editableTypeId.value }
  if (isTextEditable.value) payload.text = editableText.value
  if (isExpectedResultEditable.value) payload.expected_result = editableExpectedResult.value

  const updated = await improvementsStore.updateSuggestion(props.suggestionId, payload)
  if (updated) {
    emit('updated')
  }
}

async function onForwardToCommission() {
  if (!props.suggestionId) return
  const ok = await improvementsStore.forwardToCommission(props.suggestionId)
  if (ok) emit('updated')
}

function openReturnModal(target: 'author' | 'department-head') {
  returnTarget.value = target
  returnComment.value = ''
  showReturnModal.value = true
}

async function submitReturn() {
  if (!props.suggestionId || !returnComment.value.trim()) return
  const comment = returnComment.value.trim()
  const ok = returnTarget.value === 'department-head'
    ? await improvementsStore.returnToDepartmentHead(props.suggestionId, comment)
    : await improvementsStore.returnToAuthor(props.suggestionId, comment)
  if (ok) {
    showReturnModal.value = false
    emit('updated')
  }
}

function openRejectModal() {
  rejectComment.value = ''
  showRejectModal.value = true
}

async function submitReject() {
  if (!props.suggestionId || !rejectComment.value.trim()) return
  const ok = await improvementsStore.rejectSuggestion(props.suggestionId, rejectComment.value.trim())
  if (ok) {
    showRejectModal.value = false
    emit('updated')
  }
}

function openAcceptModal() {
  acceptComment.value = ''
  showAcceptModal.value = true
}

async function submitAccept() {
  if (!props.suggestionId || !acceptComment.value.trim()) return
  const ok = await improvementsStore.acceptSuggestion(props.suggestionId, acceptComment.value.trim())
  if (ok) {
    showAcceptModal.value = false
    emit('updated')
  }
}

const employeeSelectionModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)
const ppuExpertTree = ref<IEmployeeTreeDepartment[]>([])

async function openEmployeeSelectionModal() {
  const feedbackStore = useFeedbackStore()
  feedbackStore.isGlobalLoading = true
  try {
    ppuExpertTree.value = await improvementsStore.fetchPpuExpertsTree()
    employeeSelectionModalRef.value?.open()
  } finally {
    feedbackStore.isGlobalLoading = false
  }
}

// Appoint experts via EmployeeSelectionModal
async function onAppointExperts(selectedItems: IEmployeeTreeItem[]) {
  if (!props.suggestionId) return
  const empIds = selectedItems.map(item => item.id)
  if (!empIds.length) return

  const ok = await improvementsStore.createCommission(props.suggestionId, {
    employee_ids: empIds,
  })
  if (ok) {
    showEmployeeSelectionModal.value = false
    emit('updated')
  }
}

// Expert voting modal
function openVoteModal() {
  voteComment.value = ''
  showVoteModal.value = true
}

async function submitVote(result: 'confirmed' | 'rejected') {
  if (!props.suggestionId || !myExpertEntry.value || !myExpertEntry.value.id) return
  const ok = await improvementsStore.expertVote(
    props.suggestionId,
    myExpertEntry.value.id,
    result,
    voteComment.value.trim(),
  )
  if (ok) {
    showVoteModal.value = false
    emit('updated')
  }
}


// Delete expert entry
const expertNameToDelete = computed(() => {
  if (!expertIdToDelete.value) return ''
  const found = expertEntries.value.find(e => e.id === expertIdToDelete.value)
  return found?.responsible_employee ? getFullName(found.responsible_employee) : ''
})

function confirmDeleteExpert(entryId: number) {
  expertIdToDelete.value = entryId
  showDeleteExpertConfirm.value = true
}

async function handleDeleteExpert() {
  if (!props.suggestionId || !expertIdToDelete.value) return
  const ok = await improvementsStore.removeExpertEntry(props.suggestionId, expertIdToDelete.value)
  if (ok) {
    showDeleteExpertConfirm.value = false
    expertIdToDelete.value = null
    emit('updated')
  }
}

function getVotingStatusChip(entry: IResponsibilityEntry) {
  if (entry.agreement_result === 'confirmed') {
    return { text: 'Голос «За»', color: 'success' }
  }
  if (entry.agreement_result === 'rejected') {
    return { text: 'Голос «Против»', color: 'error' }
  }
  if (entry.status === 'completed') {
    return { text: 'Проголосовал', color: 'info' }
  }
  return { text: 'Ожидает', color: 'info' }
}

function getVotingStatusText(entry: IResponsibilityEntry) {
  if (entry.agreement_result || entry.status === 'completed') {
    return 'Проголосовал'
  }
  return 'Ожидает'
}
</script>

<template>

  <Teleport to="body">
    <div
      v-if="formEmbeddedLayout.isEmbedded.value && isOpen"
      class="entity-form-backdrop"
    />
  </Teleport>

  <VDialog
    v-model="isOpen"
    :z-index="2400"
    :max-width="formEmbeddedLayout.maxWidth.value"
    :content-class="overlayContentClass"
    :scrim="!formEmbeddedLayout.isEmbedded.value"
    persistent
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <Teleport to="body">
      <div
        v-if="formEmbeddedLayout.isEmbedded.value && isOpen"
        class="entity-form-actions"
      >
        <!-- Задачи и чат — вкладки правой панели: активная подсвечена, повторный клик скрывает панель -->
        <EntityPaneEar
          v-if="targetSuggestionId && hasTasksPane"
          kind="tasks"
          :active="isTasksPaneActive"
          @click="toggleTasksPane"
        />
        <EntityPaneEar
          v-if="entityChat.isAvailable.value"
          kind="chat"
          :active="isChatPaneActive"
          @click="toggleChatPane"
        />
        <EntityPaneEar
          v-else-if="isDemo"
          kind="chat"
          :active="isDemoChatVisible"
          @click="toggleDemoChat"
        />
        <DownloadEntityDocument
          v-if="shareSuggestionId"
          variant="ear"
          :path="`/api/improvements/suggestion/${shareSuggestionId}/document/`"
          :fallback-name="`ppu_${shareSuggestionId}`"
        />
        <ReportTemplateMenu v-if="shareSuggestionId" object-type="improvement" :object-id="shareSuggestionId" variant="ear" />
        <CopyEntityLink
          v-if="shareSuggestionId"
          variant="ear"
          :to="shareSuggestionTo"
        />
        <OnboardingLaunchButton v-if="shareSuggestionId" variant="ear" />
        <VBtn
          icon="ri-close-line"
          color="primary"
          class="entity-form-action-btn"
          aria-label="Закрыть"
          @click="handleClose"
        />
      </div>
    </Teleport>

    <VCard :class="{ 'entity-form-card h-100': formEmbeddedLayout.isEmbedded.value }">
      <div :class="formEmbeddedLayout.isEmbedded.value ? 'entity-form-body d-flex h-100 overflow-hidden' : 'entity-form-mobile-pane-body'">
        <!-- Панель кнопок разделов на мобильных устройствах НАД названием; закреплена при прокрутке -->
        <div
          v-if="!formEmbeddedLayout.isEmbedded.value && xs"
          class="entity-form-mobile-actions d-flex align-center justify-space-evenly w-100"
        >
          <VTooltip
            v-if="targetSuggestionId && hasTasksPane"
            location="bottom"
            :text="isTasksPaneActive ? 'Скрыть задачи' : 'Показать задачи'"
          >
            <template #activator="{ props: taskToggleProps }">
              <VBtn
                v-bind="taskToggleProps"
                icon="ri-list-check"
                variant="text"
                color="primary"
                @click="toggleTasksPane"
              />
            </template>
          </VTooltip>
          <VTooltip
            v-if="entityChat.isAvailable.value"
            location="bottom"
            text="Чат предложения"
          >
            <template #activator="{ props: eventChatProps }">
              <VBtn
                v-bind="eventChatProps"
                icon="ri-chat-1-line"
                variant="text"
                color="primary"
                @click="entityChat.openInSlidingChat"
              />
            </template>
          </VTooltip>
          <DownloadEntityDocument
            v-if="shareSuggestionId"
            variant="inline"
            :path="`/api/improvements/suggestion/${shareSuggestionId}/document/`"
            :fallback-name="`ppu_${shareSuggestionId}`"
          />
          <ReportTemplateMenu v-if="shareSuggestionId" object-type="improvement" :object-id="shareSuggestionId" variant="inline" />
          <CopyEntityLink
            v-if="shareSuggestionId"
            variant="inline"
            :to="shareSuggestionTo"
          />
          <OnboardingLaunchButton v-if="shareSuggestionId" variant="inline" />
          <CustomCloseButton :close-action="handleClose" />
        </div>
        <div
          :class="formEmbeddedLayout.isEmbedded.value ? 'entity-form-pane flex-grow-1 position-relative' : (isMobileTasksView ? 'position-relative d-flex flex-column' : 'position-relative d-flex flex-column flex-grow-1')"
        >
          <EntityAccessDeniedOverlay v-if="isAccessDenied" variant="form" />
          <VCardTitle
            class="d-flex pa-6"
            :class="{
              'flex-column pb-2': !formEmbeddedLayout.isEmbedded.value && xs,
              'px-4 py-3': xs,
            }"
            :style="isAccessDenied ? { position: 'relative', zIndex: 6 } : undefined"
          >

            <div class="form_card d-flex justify-space-between align-center w-100">
              <div class="form_card__title d-flex align-center ga-2">
                <span class="text-h4 font-weight-semibold">Карточка предложения по улучшению № {{ suggestion?.id || '' }}</span>
              </div>

              <!-- В обычном режиме на десктопе кнопки справа в строке -->
              <div
                v-if="!formEmbeddedLayout.isEmbedded.value && !xs"
                class="d-flex align-center gap-2"
              >
                <VTooltip
                  v-if="targetSuggestionId && hasTasksPane"
                  location="bottom"
                  :text="isTasksPaneActive ? 'Скрыть задачи' : 'Показать задачи'"
                >
                  <template #activator="{ props: taskToggleProps }">
                    <VBtn
                      v-bind="taskToggleProps"
                      icon="ri-list-check"
                      variant="text"
                      color="primary"
                      @click="toggleTasksPane"
                    />
                  </template>
                </VTooltip>
                <VTooltip
                  v-if="entityChat.isAvailable.value"
                  location="bottom"
                  text="Чат предложения"
                >
                  <template #activator="{ props: eventChatProps }">
                    <VBtn
                      v-bind="eventChatProps"
                      icon="ri-chat-1-line"
                      variant="text"
                      color="primary"
                      @click="entityChat.openInSlidingChat"
                    />
                  </template>
                </VTooltip>
                <DownloadEntityDocument
                  v-if="shareSuggestionId"
                  variant="inline"
                  :path="`/api/improvements/suggestion/${shareSuggestionId}/document/`"
                  :fallback-name="`ppu_${shareSuggestionId}`"
                />
                <ReportTemplateMenu v-if="shareSuggestionId" object-type="improvement" :object-id="shareSuggestionId" variant="inline" />
                <CopyEntityLink
                  v-if="shareSuggestionId"
                  variant="inline"
                  :to="shareSuggestionTo"
                />
                <OnboardingLaunchButton v-if="shareSuggestionId" variant="inline" />
                <CustomCloseButton :close-action="handleClose" />
              </div>
            </div>
          </VCardTitle>

          <VForm :class="(!formEmbeddedLayout.isEmbedded.value) ? (isMobileTasksView ? 'd-none' : 'd-flex flex-column flex-grow-1') : undefined" v-if="suggestion">
              <div :class="xs ? 'd-flex flex-column gap-4' : undefined">
                <div
                  v-if="xs"
                  class="d-flex align-center ga-2 flex-wrap px-4"
                >
                  <VChip
                    v-if="suggestion.status"
                    size="small"
                    variant="tonal"
                    :color="aggregatedStatusColor"
                  >
                    {{ aggregatedStatusLabel }}
                  </VChip>
                  <span
                    class="body-2 font-weight-medium"
                    :class="`text-${stageStatusColor}`"
                  >
                    {{ stageStatusLabel }}
                  </span>
                </div>

                <VTabs v-model="currentTab">
                  <div class="d-flex gap-2">
                    <VTab :value="0">Форма</VTab>
                    <VTab v-show="isReviewTabVisible" :value="1" :data-onboarding="IMPROVEMENTS_ANCHORS.form.reviewTab">Рассмотрение</VTab>
                    <VTab v-show="permissionsStore.canViewChangeHistory && hasHistoryLogs" :value="2" :data-onboarding="IMPROVEMENTS_ANCHORS.form.historyTab">История</VTab>
                  </div>

                  <div
                    v-if="!xs"
                    class="d-flex ga-2 align-center flex-wrap"
                  >
                    <VChip
                      v-if="suggestion.status"
                      size="small"
                      variant="tonal"
                      :color="aggregatedStatusColor"
                    >
                      {{ aggregatedStatusLabel }}
                    </VChip>
                    <span
                      class="body-2 font-weight-medium"
                      :class="`text-${stageStatusColor}`"
                    >
                      {{ stageStatusLabel }}
                    </span>
                  </div>
                </VTabs>
              </div>

              <VWindow
                v-model="currentTab"
                class="mt-4 flex-grow-1"
                :transition="false"
                :touch="false"
              >
                <!-- TAB 0: Форма -->
                <VWindowItem :value="0">
                  <div class="entity-form-scroll-body">
                    <VCol class="d-flex flex-column gap-4 px-0">
                      <VTextField
                        v-model="editableTitle"
                        variant="outlined"
                        density="comfortable"
                        persistent-placeholder
                        :disabled="!isTitleEditable"
                      >
                        <template #label>
                          <span>Предложение</span>
                        </template>
                      </VTextField>

                      <!-- Дропдаун "Вид ППУ" через SearchableSelectList из createdUi -->
                      <VMenu
                        v-model="showTypeMenu"
                        eager
                        :disabled="!isTypeEditable"
                        :close-on-content-click="false"
                        :max-height="350"
                        content-class="entity-form-menu"
                      >
                        <template #activator="{ props: menuProps }">
                          <VTextarea
                            :data-onboarding="IMPROVEMENTS_ANCHORS.form.type"
                            :model-value="selectedTypeName"
                            :class="{ 'invisible-readonly cursor-pointer': isTypeEditable }"
                            :append-inner-icon="isTypeEditable ? (showTypeMenu ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line') : undefined"
                            class="w-100"
                            variant="outlined"
                            density="comfortable"
                            placeholder="Выберите вид ППУ"
                            persistent-placeholder
                            readonly
                            rows="1"
                            auto-grow
                            no-resize
                            :clearable="isTypeEditable"
                            :disabled="!isTypeEditable"
                            v-bind="menuProps"
                            @click:append-inner.stop="isTypeEditable && (showTypeMenu = !showTypeMenu)"
                            @click:clear="onClearType"
                          >
                            <template #label>
                              <span>Вид ППУ</span>
                            </template>
                          </VTextarea>
                        </template>

                        <SearchableSelectList
                          :items="typeSelectItems"
                          placeholder="Поиск вида ППУ..."
                          @select="onSelectType"
                        />
                      </VMenu>

                      <VTextarea
                        v-model="editableText"
                        density="comfortable"
                        rows="3"
                        auto-grow
                        no-resize
                        variant="outlined"
                        persistent-placeholder
                        :disabled="!isTextEditable"
                      >
                        <template #label>
                          <span>Описание</span>
                        </template>
                      </VTextarea>

                      <VTextarea
                        v-model="editableExpectedResult"
                        density="comfortable"
                        rows="3"
                        auto-grow
                        no-resize
                        variant="outlined"
                        persistent-placeholder
                        :disabled="!isExpectedResultEditable"
                      >
                        <template #label>
                          <span>Результат</span>
                        </template>
                      </VTextarea>

                      <VTextarea
                        v-if="latestReviewComment?.comment"
                        :data-onboarding="IMPROVEMENTS_ANCHORS.form.decision"
                        :model-value="latestReviewComment.comment"
                        density="comfortable"
                        rows="3"
                        auto-grow
                        no-resize
                        variant="outlined"
                        persistent-placeholder
                        readonly
                      >
                        <template #label>
                          <span>{{ reviewCommentLabel }}</span>
                        </template>
                      </VTextarea>

                      <!-- Attached Files -->
                      <div class="d-flex flex-column gap-2">
                        <div
                          v-if="isFilesEditable"
                          class="d-flex gap-4"
                          :class="xs ? 'flex-column' : 'align-center flex-wrap'"
                        >
                          <VBtn
                            :block="xs"
                            :class="{ 'align-self-start flex-shrink-1': !xs }"
                            variant="outlined"
                            color="primary"
                            prepend-icon="ri-attachment-line"
                            @click="fileAttachRef?.openDialog()"
                          >
                            Прикрепить файлы
                          </VBtn>
                          <span
                            v-if="!smAndDown"
                            class="text-body-2 paste-hint"
                          >или вставьте файлы из буфера (Ctrl+V)</span>
                        </div>

                        <FileAttach
                          ref="fileAttachRef"
                          v-model:files="selectedFilesToUpload"
                          :disabled="!isFilesEditable"
                        />

                        <FileList
                          v-if="suggestion?.files?.length"
                          :items="suggestion.files"
                          :disabled="!isFilesEditable"
                          :show-delete="isFilesEditable"
                          @remove="onFileRemoved"
                        />
                      </div>
                    </VCol>
                  </div>
                </VWindowItem>

                <!-- TAB 1: Рассмотрение -->
                <VWindowItem :value="1">
                  <div class="entity-form-scroll-body">
                    <VCol class="d-flex flex-column gap-4 px-0">
                      <!-- Top Section: Председатель комиссии Input & Action Buttons (Вверху вкладки) -->
                      <div class="d-flex justify-space-between ga-4" :class="{ 'flex-column': xs }">
                        <div class="flex-grow-1 mr-4">
                          <VTextField
                            :data-onboarding="IMPROVEMENTS_ANCHORS.form.chair"
                            :model-value="chairName"
                            variant="outlined"
                            readonly
                            persistent-placeholder
                            style="max-width: 450px;"
                          >
                            <template #label>
                              <span>Председатель комиссии</span>
                            </template>
                          </VTextField>
                        </div>

                        <!-- Action buttons column on the right -->
                        <div class="d-flex flex-column ga-2 flex-shrink-0" :class="{ 'w-100': xs }">
                          <VBtn
                            v-if="availableActions.includes('commission')"
                            variant="outlined"
                            prepend-icon="ri-user-add-line"
                            :block="xs"
                            :data-onboarding="IMPROVEMENTS_ANCHORS.form.assignExperts"
                            @click="openEmployeeSelectionModal"
                          >
                            Назначить экспертов
                          </VBtn>

                          <VBtn
                            v-if="availableActions.includes('return-to-author')"
                            variant="outlined"
                            prepend-icon="ri-edit-line"
                            :block="xs"
                            @click="openReturnModal('author')"
                          >
                            Вернуть на доработку
                          </VBtn>

                          <VBtn
                            v-if="availableActions.includes('return-to-department-head')"
                            variant="outlined"
                            prepend-icon="ri-edit-line"
                            :block="xs"
                            @click="openReturnModal('department-head')"
                          >
                            Вернуть на доработку
                          </VBtn>
                        </div>
                      </div>

                      <!-- Entity Form Summary -->
                      <EntityFormSummary :items="summaryItems" />

                      <!-- Experts Table & Voting Stats -->
                      <VCard class="border overflow-hidden" elevation="0" :data-onboarding="IMPROVEMENTS_ANCHORS.form.experts">
                        <VDataTable
                          :headers="expertTableHeaders"
                          :items="expertEntries"
                          :items-per-page="-1"
                          class="no-row-gaps cursor-pointer-table custom-data-table"
                          hide-default-footer
                        >
                          <template #no-data>
                            <div class="text-center text-italic py-4 text-medium-emphasis">
                              Эксперты не назначены
                            </div>
                          </template>

                          <template #[`item.number`]="{ index }">
                            <span>{{ index + 1 }}</span>
                          </template>

                          <template #[`item.responsible_employee`]="{ item }">
                            <ShortFio
                              class="cursor-pointer"
                              :person="item.responsible_employee"
                              @click="item.responsible_employee?.id && openEmployeeProfile(item.responsible_employee.id)"
                            />
                          </template>

                          <template #[`item.created_at`]="{ item }">
                            <span>{{ formatDateTime(item.created_at) }}</span>
                          </template>

                          <template #[`item.status`]="{ item }">
                            <VChip
                              v-if="isChair"
                              :color="getVotingStatusChip(item).color"
                              size="small"
                            >
                              {{ getVotingStatusChip(item).text }}
                            </VChip>
                            <span v-else>
                              {{ getVotingStatusText(item) }}
                            </span>
                          </template>

                          <template #[`item.comment`]="{ item }">
                            <span>
                              {{ item.last_report || item.instructions || '' }}
                            </span>
                          </template>

                          <template #[`item.delete_expert`]="{ item }">
                            <VIconBtn
                              v-if="item.actions?.includes('manage')"
                              :rounded="false"
                              :size="32"
                              icon="ri-delete-bin-3-line"
                              variant="text"
                              color="secondary"
                              @click="item.id && confirmDeleteExpert(item.id)"
                            />
                          </template>
                        </VDataTable>
                      </VCard>

                      <!-- Voting Statistics Column -->
                      <div v-if="expertEntries.length" class="d-flex flex-column gap-1 body-2">
                        <span>Всего проголосовало - {{ totalVotedCount }}</span>
                        <span>За - {{ likesCount }} ({{ likesPercent }}%)</span>
                        <span>Против - {{ dislikesCount }} ({{ dislikesPercent }}%)</span>
                      </div>
                    </VCol>
                  </div>
                </VWindowItem>

                <!-- TAB 2: История -->
                <VWindowItem :value="2">
                  <div class="entity-form-scroll-body">
                    <HistoryLogsTable
                      :logs="historyLogs"
                      @click-user="openEmployeeDetail"
                    />
                  </div>
                </VWindowItem>
              </VWindow>

              <!-- Common Footer (`<footer>`) -->
              <footer class="d-flex flex-column gap-2 body-2 mt-4">
                <CustomDivider />
                <div class="d-flex gap-4" :class="xs ? 'flex-column' : 'align-start justify-space-between'">
                  <!-- Left: Author Info & Submitted Date -->
                  <div class="d-flex flex-column gap-2" :class="{ 'order-2': xs }">
                    <div class="d-flex align-center gap-2">
                      <VIcon icon="ri-user-3-line" size="16" />
                      <span
                        v-if="suggestion?.created_by"
                        class="cursor-pointer"
                        @click="openEmployeeProfile(suggestion.created_by.id)"
                      >
                        Автор: {{ getFullName(suggestion.created_by) }}
                      </span>
                      <span v-else>
                        Автор: Анонимно
                      </span>
                      <VTooltip v-if="suggestion?.department?.name">
                        <template #activator="{ props: actProps }">
                          <VIcon icon="ri-information-line" size="12" v-bind="actProps" />
                        </template>
                        <span>{{ suggestion.department.name }}</span>
                      </VTooltip>
                    </div>

                    <div class="d-flex align-center gap-2">
                      <VIcon icon="ri-time-line" size="16" />
                      <span>Дата и время подачи: {{ formatDateTime(suggestion?.created_at) }}</span>
                    </div>
                  </div>


                  <!-- Right: Role-Based Action Buttons (строго по массиву actions бэкенда) -->
                  <div
                    :data-onboarding="IMPROVEMENTS_ANCHORS.form.actions"
                    class="d-flex gap-2"
                    :class="xs ? 'flex-column order-1' : 'align-center flex-wrap'"
                  >
                    <!-- Действие "edit" -> Кнопка "Сохранить" (активна только при наличии несохраненных изменений) -->
                    <VBtn
                      v-if="availableActions.includes('edit') && currentTab === 0"
                      variant="elevated"
                      color="primary"
                      :block="xs"
                      :disabled="!isFormDirty"
                      @click="onSaveForm"
                    >
                      Сохранить
                    </VBtn>

                    <!-- Действие "forward-to-commission" -> Кнопка "Согласовать" -->
                    <VBtn
                      v-if="availableActions.includes('forward-to-commission')"
                      variant="elevated"
                      color="primary"
                      :block="xs"
                      @click="onForwardToCommission"
                    >
                      Согласовать
                    </VBtn>

                    <!-- Действие "accept" -> Кнопка "Принять" -->
                    <VBtn
                      v-if="availableActions.includes('accept')"
                      variant="elevated"
                      color="primary"
                      :block="xs"
                      @click="openAcceptModal"
                    >
                      Принять
                    </VBtn>

                    <!-- Действие "return-to-author" -> Кнопка "Вернуть на доработку" (в футере показывается если НЕ активна вкладка рассмотрения) -->
                    <VBtn
                      v-if="availableActions.includes('return-to-author') && !isReviewTabVisible"
                      variant="outlined"
                      :block="xs"
                      @click="openReturnModal('author')"
                    >
                      Вернуть на доработку
                    </VBtn>

                    <VBtn
                      v-if="availableActions.includes('return-to-department-head') && !isReviewTabVisible"
                      variant="outlined"
                      :block="xs"
                      @click="openReturnModal('department-head')"
                    >
                      Вернуть на доработку
                    </VBtn>

                    <!-- Действие "reject" -> Кнопка "Отклонить" -->
                    <VBtn
                      v-if="availableActions.includes('reject')"
                      variant="outlined"
                      :block="xs"
                      @click="openRejectModal"
                    >
                      Отклонить
                    </VBtn>

                    <!-- Голосование эксперта -->
                    <VBtn
                      v-if="canVote"
                      :data-onboarding="IMPROVEMENTS_ANCHORS.form.vote"
                      variant="elevated"
                      color="primary"
                      :block="xs"
                      @click="openVoteModal"
                    >
                      Голосовать
                    </VBtn>
                  </div>
                </div>
              </footer>
            </VForm>
        </div>

        <!-- Разделитель между формой и правой панелью — перетягивается мышкой -->
        <div
          v-if="formEmbeddedLayout.isEmbedded.value && ((isTasksPaneVisible && hasTasksPane) || (entityChat.isChatVisible.value && entityChat.isAvailable.value) || isDemoChatVisible)"
          class="entity-form-resizer"
          :class="{ 'is-resizing': chatPaneResizer.isResizing.value }"
          @mousedown="chatPaneResizer.startResize"
        />
        <!-- Панель задач (TaskCard) или встроенный чат группы ППУ справа (чат — только embedded-режим; без embedded задачи открываются под шапкой формы вместо её тела) -->
        <div
          v-if="isTasksPaneActive"
          :class="formEmbeddedLayout.isEmbedded.value
            ? 'entity-form-chat-pane overflow-hidden pa-0 d-flex flex-column'
            : 'd-flex flex-column flex-grow-1'"
          :style="formEmbeddedLayout.isEmbedded.value ? { flexBasis: `${chatPaneResizer.width.value}px` } : undefined"
        >
          <TaskCard
            class="flex-grow-1"
            v-if="coordinatorTask"
            :task="coordinatorTask"
            :is-demo="isDemo"
            @updated="handleCoordinatorTaskUpdated"
          />
        </div>
        <BridgedEmbeddedChat
          v-else-if="formEmbeddedLayout.isEmbedded.value && entityChat.isChatVisible.value && entityChat.isAvailable.value"
          :chat-id="entityChat.chatId.value"
          :connect-error="entityChat.connectError.value"
          class="entity-form-chat-pane"
          :style="{ flexBasis: `${chatPaneResizer.width.value}px` }"
          @hide-chat="entityChat.hideChat"
        />
        <OnboardingDemoChat
          v-else-if="formEmbeddedLayout.isEmbedded.value && isDemoChatVisible && demoItem"
          :chat="demoItem.chat"
          class="entity-form-chat-pane"
          :style="{ flexBasis: `${chatPaneResizer.width.value}px` }"
        />
      </div>
    </VCard>
  </VDialog>


  <!-- EmployeeSelectionModal for appointing experts -->
  <EmployeeSelectionModal
    ref="employeeSelectionModalRef"
    v-model="selectedExpertId"
    :employees="ppuExpertTree"
    :filtered="true"
    :multiple="true"
    title="Выберите экспертов для комиссии"
    @select-multiple="onAppointExperts"
  />


  <!-- Modal: Комментарий к доработке ППУ №N -->
  <VDialog
    v-model="showReturnModal"
    max-width="38rem"
    content-class="entity-form-menu"
  >
    <VCard>
      <VCardTitle class="d-flex pa-6">
        <div class="form_card d-flex justify-space-between w-100">
          <div class="form_card__title">
            <span class="text-h4">Комментарий к доработке ППУ № {{ suggestion?.id || '' }}</span>
          </div>
        </div>
        <CustomCloseButton :close-action="() => showReturnModal = false" />
      </VCardTitle>

      <VCardText>
        <div class="entity-form-scroll-body d-flex flex-column gap-4">
          <EntityFormSummary
            v-if="suggestion"
            :items="summaryItems"
          />

          <VTextarea
            v-model="returnComment"
            variant="outlined"
            density="comfortable"
            rows="3"
            placeholder="Опишите, что нужно изменить или доработать"
            persistent-placeholder
          >
            <template #label>
              <span>Комментарий к доработке</span>
            </template>
          </VTextarea>
        </div>

        <footer class="d-flex gap-2 mt-4" :class="xs ? 'flex-column' : 'justify-end'">
          <VBtn
            variant="elevated"
            color="primary"
            :block="xs"
            :disabled="!returnComment.trim()"
            @click="submitReturn"
          >
            Отправить
          </VBtn>
        </footer>
      </VCardText>
    </VCard>
  </VDialog>

  <!-- Modal: Отклонение ППУ №N -->
  <VDialog
    v-model="showRejectModal"
    max-width="38rem"
    content-class="entity-form-menu"
  >
    <VCard>
      <VCardTitle class="d-flex pa-6">
        <div class="form_card d-flex justify-space-between w-100">
          <div class="form_card__title">
            <span class="text-h4">Отклонение ППУ № {{ suggestion?.id || '' }}</span>
          </div>
        </div>
        <CustomCloseButton :close-action="() => showRejectModal = false" />
      </VCardTitle>

      <VCardText>
        <div class="entity-form-scroll-body d-flex flex-column gap-4">
          <EntityFormSummary
            v-if="suggestion"
            :items="summaryItems"
          />

          <VTextarea
            v-model="rejectComment"
            variant="outlined"
            density="comfortable"
            rows="3"
            placeholder="Укажите обоснование принятого решения"
            persistent-placeholder
          >
            <template #label>
              <span>Решение</span>
            </template>
          </VTextarea>
        </div>

        <footer class="d-flex gap-2 mt-4" :class="xs ? 'flex-column' : 'justify-end'">
          <VBtn
            variant="elevated"
            color="primary"
            :block="xs"
            :disabled="!rejectComment.trim()"
            @click="submitReject"
          >
            Отклонить
          </VBtn>
        </footer>
      </VCardText>
    </VCard>
  </VDialog>

  <!-- Modal: Принятие ППУ №N -->
  <VDialog
    v-model="showAcceptModal"
    max-width="38rem"
    content-class="entity-form-menu"
  >
    <VCard>
      <VCardTitle class="d-flex pa-6">
        <div class="form_card d-flex justify-space-between w-100">
          <div class="form_card__title">
            <span class="text-h4">Принятие ППУ № {{ suggestion?.id || '' }}</span>
          </div>
        </div>
        <CustomCloseButton :close-action="() => showAcceptModal = false" />
      </VCardTitle>

      <VCardText>
        <div class="entity-form-scroll-body d-flex flex-column gap-4">
          <EntityFormSummary
            v-if="suggestion"
            :items="summaryItems"
          />

          <VTextarea
            v-model="acceptComment"
            variant="outlined"
            density="comfortable"
            rows="3"
            placeholder="Укажите обоснование принятого решения"
            persistent-placeholder
          >
            <template #label>
              <span>Решение</span>
            </template>
          </VTextarea>
        </div>

        <footer class="d-flex gap-2 mt-4" :class="xs ? 'flex-column' : 'justify-end'">
          <VBtn
            variant="elevated"
            color="primary"
            :block="xs"
            :disabled="!acceptComment.trim()"
            @click="submitAccept"
          >
            Принять
          </VBtn>
        </footer>
      </VCardText>
    </VCard>
  </VDialog>

  <!-- Modal: Голосование по предложению №N (Expert Voting Modal with Colored Buttons) -->
  <VDialog
    v-model="showVoteModal"
    max-width="38rem"
    content-class="entity-form-menu"
  >
    <VCard>
      <VCardTitle class="d-flex pa-6">
        <div class="form_card d-flex justify-space-between w-100">
          <div class="form_card__title">
            <span class="text-h4">Голосование по предложению № {{ suggestion?.id || '' }}</span>
          </div>
        </div>
        <CustomCloseButton :close-action="() => showVoteModal = false" />
      </VCardTitle>

      <VCardText>
        <div class="entity-form-scroll-body d-flex flex-column gap-4">
          <EntityFormSummary
            v-if="suggestion"
            :items="summaryItems"
          />

          <VTextarea
            v-model="voteComment"
            variant="outlined"
            density="comfortable"
            rows="3"
            placeholder="Укажите обоснование принятого решения"
            persistent-placeholder
          >
            <template #label>
              <span>Решение</span>
            </template>
          </VTextarea>
        </div>

        <footer class="d-flex gap-3 mt-4" :class="xs ? 'flex-column' : 'justify-end'">
          <VBtn
            color="error"
            variant="tonal"
            :block="xs"
            @click="submitVote('rejected')"
          >
            Голосовать «Против»
          </VBtn>
          <VBtn
            color="success"
            variant="tonal"
            :block="xs"
            @click="submitVote('confirmed')"
          >
            Голосовать «За»
          </VBtn>
        </footer>
      </VCardText>
    </VCard>
  </VDialog>

  <!-- Confirm Dialog for deleting expert -->
  <ConfirmDialog
    v-model:is-dialog-visible="showDeleteExpertConfirm"
    confirmation-question="Удалить эксперта из комиссии?"
    :confirmation-text="expertNameToDelete ? `Вы действительно хотите удалить эксперта «${expertNameToDelete}» из комиссии? Это действие нельзя отменить.` : 'Это действие нельзя отменить.'"
    confirm-btn="Удалить"
    reject-btn="Отмена"
    @confirm="(ok) => { if (ok) handleDeleteExpert(); else showDeleteExpertConfirm = false }"
  />

  <!-- Employee Profile Sidebar -->
  <EmployeeProfileSidebar
    v-model="isEmployeeInfoModalOpen"
    :employee-id="selectedEmployeeId || ''"
  />

  <!-- Confirm Dialog for unsaved changes -->
  <ConfirmDialog
    v-model:is-dialog-visible="showUnsavedChangesDialog"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Внесенные изменения не сохранятся"
    confirm-btn="Закрыть"
    reject-btn="Отмена"
    @confirm="(ok) => { if (ok) forceClose(); else showUnsavedChangesDialog = false }"
  />
</template>

<style lang="scss" scoped>
:deep(.v-slide-group__content) {
  justify-content: space-between;
}

:deep(.custom-data-table .v-data-table__th),
:deep(.custom-data-table th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
  white-space: nowrap;
}

:deep(.custom-data-table .v-data-table__td),
:deep(.custom-data-table td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.custom-data-table th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}
</style>
